import { useMemo, useState } from 'react';
import { Building2, Cog, Package } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardTitle, Chip, Select } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { formatBRL, formatDateBR } from '@/lib/format';
import { ApiError } from '@/lib/api-client';
import { anoIRPF } from '../irpf-anos';
import { useDocumentos, useBensDireitos } from '../api';
import type { BemDireito } from '../schemas';

interface Props {
  cpf: string;
}

// Agrupamento por grupo IRPF (campo `grupo`) ou fallback por código.
// O banco grava o código do grupo (01 = imóveis, 02 = participações, etc.)
// mas alguns registros antigos usam subcodigo (11-17).
function bucketize(b: BemDireito): 'imoveis' | 'maquinas' | 'outros' {
  const grupo = (b.grupo ?? '').toLowerCase();
  if (grupo.includes('imóv') || grupo.includes('imovel') || grupo.includes('imov')) return 'imoveis';
  if (grupo.includes('máquina') || grupo.includes('maquina') || grupo.includes('equipamento') || grupo.includes('veículo') || grupo.includes('veiculo')) return 'maquinas';
  // fallback por código numérico (subcodigos legados)
  const c = (b.codigo ?? '').padStart(2, '0');
  const n = parseInt(c, 10);
  if (n >= 11 && n <= 17) return 'imoveis';
  if (n === 21 || n === 22 || (n >= 51 && n <= 53)) return 'maquinas';
  return 'outros';
}

export function ClienteBensTab({ cpf }: Props) {
  const { data = [], isLoading, error } = useBensDireitos(cpf);
  const {data: documentos=[]} = useDocumentos(cpf);
  const irpfs = useMemo(()=>documentos.filter(d=>anoIRPF(d)!=null),[documentos]);

  // Um bem aparece uma vez POR DECLARAÇÃO — é assim que o IRPF funciona, não é
  // duplicata. Sem separar o exercício, um cliente com 2025 e 2024 importados
  // mostrava o mesmo imóvel duas vezes e somava os dois anos no total (o
  // Ronaldo José Fontana aparecia com R$ 3.433.083,92 em vez de R$ 2.113.429,12,
  // 62% a mais). Nunca apague a linha do ano anterior: ela é o histórico.
  const anos = useMemo(() => {
    const set = new Set<number>(irpfs.map(d=>anoIRPF(d)!));
    for (const b of data) {
      const n = Number(b.ano_calendario);
      if (Number.isInteger(n) && n >= 1900) {
        set.add(n);
        if (b.valor_ano_anterior != null) set.add(n - 1);
      }
    }
    return [...set].sort((a, b) => b - a);
  }, [data, irpfs]);

  const [exercicio, setExercicio] = useState<number | null>(null);
  const anoAtivo = exercicio != null && anos.includes(exercicio) ? exercicio : (anos[0] ?? null);

  const { visiveis, usandoAnoAnterior } = useMemo(() => {
    if (anoAtivo == null) return { visiveis: data, usandoAnoAnterior: false };

    const declaradosNoAno = data.filter((b) => Number(b.ano_calendario) === anoAtivo);
    if (declaradosNoAno.length > 0) {
      return { visiveis: declaradosNoAno, usandoAnoAnterior: false };
    }

    const informadosComoAnoAnterior = data.filter(
      (b) => Number(b.ano_calendario) === anoAtivo + 1 && b.valor_ano_anterior != null,
    );
    return { visiveis: informadosComoAnoAnterior, usandoAnoAnterior: true };
  }, [data, anoAtivo]);

  const grupos = useMemo(() => {
    const out: Record<'imoveis' | 'maquinas' | 'outros', BemDireito[]> = {
      imoveis: [],
      maquinas: [],
      outros: [],
    };
    for (const b of visiveis) out[bucketize(b)].push(b);
    return out;
  }, [visiveis]);

  const totalExercicio = visiveis.reduce(
    (s, b) => s + (usandoAnoAnterior ? (b.valor_ano_anterior ?? 0) : (b.valor_ano_atual ?? 0)),
    0,
  );
  const totalAnterior = usandoAnoAnterior
    ? null
    : visiveis.reduce((s, b) => s + (b.valor_ano_anterior ?? 0), 0);
  const documentosDoAno = irpfs.filter((d) => {
    const anoDocumento = anoIRPF(d);
    return anoDocumento === anoAtivo || (usandoAnoAnterior && anoDocumento === anoAtivo! + 1);
  });

  if (isLoading) {
    return (
      <Card padding="md">
        <p className="text-sm text-text-muted">Carregando bens & direitos…</p>
      </Card>
    );
  }
  if (error) {
    return (
      <Card padding="md">
        <p className="text-sm text-danger">
          {error instanceof ApiError ? error.message : 'Falha ao carregar IRPF.'}
        </p>
      </Card>
    );
  }
  if (data.length === 0 && !irpfs.length) {
    return (
      <Card padding="md">
        <p className="text-sm text-text-muted">
          Sem bens & direitos cadastrados para este cliente.
        </p>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      <Card padding="md">
        <Stack gap="sm">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle>Resumo IRPF</CardTitle>
            {anos.length > 1 ? (
              <div className="flex items-center gap-2">
                <label htmlFor="cli-bens-exercicio" className="text-[0.72rem] uppercase tracking-wider text-text-2">
                  Ano-calendário
                </label>
                <Select
                  id="cli-bens-exercicio"
                  value={String(anoAtivo ?? '')}
                  onChange={(e) => setExercicio(Number(e.target.value))}
                  className="text-[0.82rem] w-auto"
                >
                  {anos.map((a) => (
                    <option key={a} value={a}>
                      {a} · declaração {a + 1}
                    </option>
                  ))}
                </Select>
                <Chip tone="neutral" size="sm">
                  {anos.length} exercícios
                </Chip>
              </div>
            ) : (
              anoAtivo != null && (
                <Chip tone="neutral" size="sm">
                  Ano-calendário {anoAtivo}
                </Chip>
              )
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {documentosDoAno.sort((a,b)=>(b.created_at??'').localeCompare(a.created_at??'')).map(d=><a key={String(d.id)} href={`/api/v2/documentos/${d.id}/arquivo`} target="_blank" rel="noreferrer" className="text-accent underline">{String(d.descricao ?? d.tipo ?? 'Abrir IRPF completo')}</a>)}
          </div>
          {visiveis.length===0 ? <p>Declaração anexada. Bens e valores deste ano ainda não extraídos; consulte o documento completo acima.</p> : <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <Metric label="Itens" value={visiveis.length} />
            <Metric label="Imóveis" value={grupos.imoveis.length} />
            <Metric label="Máquinas" value={grupos.maquinas.length} />
            <Metric
              label={anoAtivo != null ? `Total em ${anoAtivo}` : 'Total do exercício'}
              value={totalExercicio > 0 ? formatBRL(totalExercicio) : '—'}
            />
            {totalAnterior != null && (
              <Metric
                label={anoAtivo != null ? `Total em ${anoAtivo - 1}` : 'Total ano anterior'}
                value={totalAnterior > 0 ? formatBRL(totalAnterior) : '—'}
              />
            )}
          </div>}
        </Stack>
      </Card>

      <BensGrupo title="Imóveis" icon={Building2} items={grupos.imoveis} showImovel />
      <BensGrupo title="Máquinas & equipamentos" icon={Cog} items={grupos.maquinas} />
      <BensGrupo title="Outros bens" icon={Package} items={grupos.outros} />
    </Stack>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wider text-text-muted">{label}</span>
      <span className="text-base font-semibold tabular-nums">{value}</span>
    </div>
  );
}

interface GrupoProps {
  title: string;
  icon: LucideIcon;
  items: BemDireito[];
  showImovel?: boolean;
}

function BensGrupo({ title, icon: Icon, items, showImovel }: GrupoProps) {
  if (items.length === 0) return null;
  return (
    <Card padding="md">
      <Stack gap="sm">
        <div className="flex items-center gap-2">
          <Icon size={14} strokeWidth={1.5} className="text-text-2" />
          <CardTitle>{title}</CardTitle>
          <Chip tone="neutral" size="sm">
            {items.length}
          </Chip>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-border">
              <tr className="text-left text-text-muted">
                <th className="py-1.5 px-2 font-semibold uppercase tracking-wider text-[10px]">
                  Código
                </th>
                <th className="py-1.5 px-2 font-semibold uppercase tracking-wider text-[10px]">
                  Discriminação
                </th>
                <th className="py-1.5 px-2 font-semibold uppercase tracking-wider text-[10px]">
                  Município/UF
                </th>
                {showImovel && (
                  <th className="py-1.5 px-2 font-semibold uppercase tracking-wider text-[10px]">
                    Matrícula
                  </th>
                )}
                <th className="py-1.5 px-2 font-semibold uppercase tracking-wider text-[10px] text-right">
                  Aquisição
                </th>
                <th className="py-1.5 px-2 font-semibold uppercase tracking-wider text-[10px] text-right">
                  Valor
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((b, i) => (
                <tr key={`${b.id ?? i}`} className="border-b border-border last:border-0">
                  <td className="py-1.5 px-2 font-mono text-[10px]">{b.codigo ?? '—'}</td>
                  <td className="py-1.5 px-2 text-text">
                    <div className="flex flex-col">
                      <span className="line-clamp-2">{b.discriminacao ?? '—'}</span>
                      {b.area_total_ha != null && (
                        <span className="text-[10px] text-text-muted">
                          {b.area_total_ha.toLocaleString('pt-BR')} ha
                          {b.cartorio && ` · ${b.cartorio}`}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-1.5 px-2 text-text-2">
                    {b.municipio ? `${b.municipio}/${b.uf ?? ''}` : '—'}
                  </td>
                  {showImovel && (
                    <td className="py-1.5 px-2 font-mono text-[10px] text-text-2">
                      {b.matricula ?? '—'}
                    </td>
                  )}
                  <td className="py-1.5 px-2 text-right text-text-muted">
                    {b.data_aquisicao ? formatDateBR(b.data_aquisicao) : '—'}
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums text-text font-medium">
                    {b.valor_ano_atual != null ? formatBRL(b.valor_ano_atual) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Stack>
    </Card>
  );
}
